import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs, BreadcrumbItem } from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faHome, 
  faChartBar, 
  faCog, 
  faDatabase, 
  faFileAlt, 
  faUsers, 
  faShield,
  faClipboardCheck,
  faBuilding,
  faFlask,
  faWater,
  faLeaf,
  faRecycle,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
// import Notification from "./Notification/Notification";

const BreadCrumbsPage = ({ children, customBreadcrumbs }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Update time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Icon mapping for different sections
  const getIconForPath = (segment) => {
    const iconMap = {
      'dashboard': faHome,
      'metrics': faChartBar,
      'settings': faCog,
      'reference-data': faDatabase,
      'evidences': faFileAlt,
      'frameworks': faShield,
      'policies': faClipboardCheck,
      'organization': faBuilding,
      'reports': faClipboardCheck,
      'analytics': faChartBar,
      'ghg-emissions': faLeaf,
      'energy': faFlask,
      'water': faWater,
      'waste': faRecycle,
      'health-safety': faShield,
      'human-rights-labor': faUsers,
      'assessments': faClipboardCheck,
      'logs': faFileAlt,
      'controls': faCog,
    };
    return iconMap[segment] || faChevronRight;
  };

  // Clean and format path segments
  const formatSegment = (segment) => {
    return segment
      .replace(/-/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Generate breadcrumb items from URL path
  const generateBreadcrumbs = () => {
    if (customBreadcrumbs) {
      return customBreadcrumbs;
    }

    const pathSegments = location.pathname.split('/').filter(segment => segment !== '');
    const breadcrumbItems = [];

    // Always start with Home/Dashboard
    breadcrumbItems.push({
      label: 'Dashboard',
      href: `/${pathSegments[0] || 'default'}`,
      icon: faHome,
      isClickable: true
    });

    let currentPath = `/${pathSegments[0] || 'default'}`;

    // Process remaining segments (skip the first org ID segment)
    for (let i = 1; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      currentPath += `/${segment}`;

      // Skip numeric IDs, very long segments (likely IDs), "metrics" segment, and "dashboard" segment to avoid duplication
      if (/^\d+$/.test(segment) || segment.length > 20 || segment === "metrics" || segment === "dashboard") {
        continue;
      }

      // Special handling for certain paths
      let label = formatSegment(segment);
      let isClickable = i < pathSegments.length - 1; // Last item is not clickable

      // Override labels for specific cases
      if (segment === 'create') {
        label = 'Create New';
        isClickable = false;
      } else if (segment === 'edit') {
        label = 'Edit';
        isClickable = false;
      } else if (params._metricId && segment === params._metricId) {
        label = formatSegment(params._metricId);
        isClickable = false;
      }

      breadcrumbItems.push({
        label,
        href: isClickable ? currentPath : null,
        icon: getIconForPath(segment),
        isClickable
      });
    }

    return breadcrumbItems;
  };

  const breadcrumbItems = generateBreadcrumbs();

  const handleBreadcrumbClick = (href) => {
    if (href) {
      navigate(href);
    }
  };

  if (breadcrumbItems.length <= 1) {
    // Don't show breadcrumbs if there's only home or less
    return (
      <div className="w-full flex flex-col">
        {children}
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col">
      {/* Combined Breadcrumb Navigation with Date/Time only */}
      <div className="bg-slate-50 border-b border-slate-200 px-8 py-2">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            {/* Left side - Breadcrumbs */}
            <div className="flex-1">
              <Breadcrumbs
                size="sm"
                separator={<FontAwesomeIcon icon={faChevronRight} className="text-slate-400 text-xs" />}
                classNames={{
                  list: "gap-2",
                  item: "data-[current=true]:opacity-100 opacity-70",
                  separator: "px-2"
                }}
              >
                {breadcrumbItems.map((item, index) => (
                  <BreadcrumbItem
                    key={index}
                    className={`
                      flex items-center gap-2 text-sm
                      ${item.isClickable 
                        ? 'text-slate-600 hover:text-emerald-600 cursor-pointer transition-colors' 
                        : 'text-slate-900 font-medium'
                      }
                    `}
                    onClick={() => handleBreadcrumbClick(item.href)}
                    isCurrent={!item.isClickable}
                  >
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon 
                        icon={item.icon} 
                        className={`text-xs ${
                          item.isClickable ? 'text-slate-500' : 'text-emerald-600'
                        }`} 
                      />
                      <span>{item.label}</span>
                    </div>
                  </BreadcrumbItem>
                ))}
              </Breadcrumbs>
            </div>

            {/* Right side - Date/Time only */}
            <div className="flex items-center gap-6">
              {/* Date and Time */}
              <div className="text-xs text-slate-600">
                {formatTime(currentTime)} • {formatDate(currentTime)}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Page Content */}
      <div className="flex-1">
        {children}
      </div>
    </div>
  );
};

export default BreadCrumbsPage;
